/**
 * castVisualPlan — a cast's geometry plus a caster pose → the exact `vfx:*`
 * EventBus emits that draw it (plan 29 F5, lane BV 2026-10-11).
 *
 * The local behaviours (AOEBehavior, SummonBehavior, the cone and line AOEs,
 * MeleeBehavior's swing, the caster aura) emit their visuals through these
 * builders; RemoteCastGeometryPlayer emits the same plan for another player's
 * cast. Same geometry + same pose → the same payloads → the same pixels.
 *
 * A plan is presentation only: every entry is a `vfx:*` event. Damage, area
 * hits and hit sounds stay with the caster's behaviours and the server
 * (remote-visuals rule: an observer never sends damage for someone else's
 * cast).
 */
import type { EventPayloadMap } from '../../../shared/EventBus/EventPayloadMap';
import { MELEE_SWING_CONFIG } from '../../../config/abilities/meleeSwing.config';
import { meleeSwingLook } from './meleeSwingLook';
import { abilityAreaColor, AREA_MARKER_MIN_MS } from '../area/areaMarker';
import {
  forwardOf,
  type AreaGeometry, type CastGeometry, type ConeGeometry, type LineGeometry,
  type SwingGeometry, type ZoneGeometry, type CasterAuraGeometry, type OrbitGeometry, type BeatGeometry,
} from './castGeometry';

type VfxEvent = Extract<keyof EventPayloadMap, `vfx:${string}`>;

/** One emit: `event` with `payload`, `delayMs` after the cast. */
export interface VisualEmit<E extends VfxEvent = VfxEvent> {
  event: E;
  payload: EventPayloadMap[E];
  delayMs: number;
}

/** Who cast, where their body is and which way they face. */
export interface CastPose {
  abilityId: string;
  casterId: string;
  heroClass?: string;
  /** Body centre (`playerCombatPoint`). */
  body: { x: number; y: number };
  facingRight: boolean;
}

const emit = <E extends VfxEvent>(event: E, payload: EventPayloadMap[E], delayMs = 0): VisualEmit => ({ event, payload, delayMs } as VisualEmit);

let seq = 0;
/** A unique instance id for a placed ring / zone. */
export function nextInstanceId(prefix: string, abilityId: string, casterId: string): string {
  return `${prefix}_${abilityId}_${casterId}_${++seq}`;
}

/** The real hit-area ring (areaMarker contract) as plan entries. */
export function areaMarkerPlan(abilityId: string, instanceId: string, at: { x: number; y: number }, radius: number, windowMs: number): VisualEmit[] {
  return [
    emit('vfx:summon_placed', { type: 'zone', x: at.x, y: at.y, radius, instanceId, color: abilityAreaColor(abilityId), exact: true }),
    emit('vfx:summon_expired', { instanceId }, Math.max(AREA_MARKER_MIN_MS, windowMs)),
  ];
}

/** The melee swing payload (lane AZ look, D55 cone) for a body and facing. */
export function meleeSwingPayload(
  abilityId: string, heroClass: string | undefined, body: { x: number; y: number }, facingRight: boolean,
  swing: SwingGeometry, targetId?: string,
): EventPayloadMap['vfx:melee_swing'] {
  const look = meleeSwingLook(abilityId, heroClass, swing.lineColor);
  return {
    x: body.x,
    y: body.y,
    isRight: facingRight,
    color: look.color,
    arcAngle: swing.arcAngle,
    arcSpread: swing.arcSpread,
    arcRadius: look.reach,
    lineWidth: swing.lineWidth,
    duration: swing.durationMs,
    style: look.style,
    targetId,
    abilityId,
    cone: { rangePx: look.cone.rangePx, arcDeg: look.cone.arcDeg, upDeg: look.cone.upDeg, downDeg: look.cone.downDeg },
  };
}

/** The swing geometry a MeleeConfig + its metadata describe (defaults from MELEE_SWING_CONFIG). */
export function swingGeometryOf(
  config: { arcAngle?: number; arcSpread?: number },
  melee?: { lineWidth?: number; swingDuration?: number; lineColor?: number },
): SwingGeometry {
  return {
    arcAngle: config.arcAngle ?? MELEE_SWING_CONFIG.arcAngle,
    arcSpread: config.arcSpread ?? MELEE_SWING_CONFIG.arcSpread,
    lineWidth: melee?.lineWidth ?? MELEE_SWING_CONFIG.lineWidth,
    durationMs: melee?.swingDuration ?? MELEE_SWING_CONFIG.durationMs,
    ...(melee?.lineColor !== undefined ? { lineColor: melee.lineColor } : {}),
  };
}

/** Where an area lands: the body centre, `forwardPx` toward the facing. */
export function areaCentre(area: Pick<AreaGeometry, 'forwardPx'>, pose: Pick<CastPose, 'body' | 'facingRight'>): { x: number; y: number } {
  return forwardOf(pose.body, pose.facingRight, area.forwardPx);
}

/** An area's visuals: its ring, the wind-up streak, and the zone at its radius. */
export function areaPlan(area: AreaGeometry, abilityId: string, casterId: string, centre: { x: number; y: number }): VisualEmit[] {
  const plan = areaMarkerPlan(abilityId, nextInstanceId('area', abilityId, casterId), centre, area.radius, area.markerMs ?? area.windUpMs + area.ticks * area.intervalMs);
  const hasWindUp = !!area.windUpEffectId && area.windUpMs > 0;
  if (hasWindUp) {
    plan.push(emit('vfx:spawn', { effectId: area.windUpEffectId!, x: centre.x, y: centre.y, duration: area.windUpMs, abilityId }));
  }
  // The zone lands when the wind-up ends, with its first tick — whether or not
  // a wind-up effect draws the fall (lane BY: Rain of Arrows drew its volley at
  // the press while its damage waited out the 800 ms draw).
  if (area.effectId) {
    plan.push(emit('vfx:spawn', {
      effectId: area.effectId, x: centre.x, y: centre.y,
      duration: area.ticks * area.intervalMs + 100, areaRadius: area.radius, abilityId,
    }, area.windUpMs > 0 ? area.windUpMs : 0));
  }
  return plan;
}

/** Where a zone is placed: on the body, or `forwardPx` toward the facing. */
export function zonePlacement(zone: Pick<ZoneGeometry, 'forwardPx'>, pose: Pick<CastPose, 'body' | 'facingRight'>): { x: number; y: number } {
  return forwardOf(pose.body, pose.facingRight, zone.forwardPx);
}

/** A placed zone's visuals: its persistent effect, its ring, and its expiry (`withExpiry` false when the owner expires it). */
export function zonePlan(zone: ZoneGeometry, abilityId: string, at: { x: number; y: number }, instanceId: string, withExpiry = true): VisualEmit[] {
  const plan: VisualEmit[] = [];
  if (zone.effectId) {
    plan.push(emit('vfx:spawn', {
      effectId: zone.effectId, x: at.x, y: at.y, instanceId, abilityId,
      ...(zone.durationMs > 0 ? { duration: zone.durationMs } : {}), follow: false,
    }));
  }
  // Plan 29 F9 (lane CA): a totem's ring wears its ability's colour (every
  // totem was the same summon green — Searing Totem's fire ring included).
  const ringColor = zone.kind === 'totem' ? abilityAreaColor(abilityId) : undefined;
  plan.push(emit('vfx:summon_placed', { type: zone.kind, x: at.x, y: at.y, radius: zone.radius, instanceId, ...(ringColor !== undefined ? { color: ringColor } : {}) }));
  if (withExpiry) plan.push(emit('vfx:summon_expired', { instanceId }, zone.durationMs));
  return plan;
}

/** A zone's trigger: its burst (world-anchored one-shot), then the ring flashes out. */
export function zoneTriggerEmit(zone: ZoneGeometry, abilityId: string, at: { x: number; y: number }, instanceId: string, delayMs = 0): VisualEmit[] {
  const plan: VisualEmit[] = zone.triggerEffectId
    ? [emit('vfx:spawn', { effectId: zone.triggerEffectId, x: at.x, y: at.y, abilityId, follow: false }, delayMs)]
    : [];
  plan.push(emit('vfx:summon_triggered', { instanceId }, delayMs));
  return plan;
}

/** A cone's visuals from the body centre: the real wedge and its effect. */
export function conePlan(cone: ConeGeometry, abilityId: string, pose: Pick<CastPose, 'body' | 'facingRight'>): VisualEmit[] {
  const { body, facingRight } = pose;
  const plan: VisualEmit[] = [
    emit('vfx:area_cone', { x: body.x, y: body.y, facing: facingRight ? 'right' : 'left', rangePx: cone.distance, arcDeg: cone.angle, abilityId }),
  ];
  if (cone.effectId) {
    plan.push(emit('vfx:spawn', {
      effectId: cone.effectId, x: body.x, y: body.y, flipX: !facingRight, areaRadius: cone.distance, abilityId, follow: false,
    }));
  }
  return plan;
}

/** The centre of a line: half its length ahead of the body centre. */
export function lineCentre(line: Pick<LineGeometry, 'length'>, pose: Pick<CastPose, 'body' | 'facingRight'>): { x: number; y: number } {
  return forwardOf(pose.body, pose.facingRight, line.length * 0.5);
}

/** A line's effect at its centre. */
export function linePlan(line: LineGeometry, at: { x: number; y: number }): VisualEmit[] {
  return line.effectId ? [emit('vfx:spawn', { effectId: line.effectId, x: at.x, y: at.y })] : [];
}

/** The caster aura: on for its lifetime, then off. */
export function casterAuraPlan(aura: CasterAuraGeometry, abilityId: string, casterId: string): VisualEmit[] {
  return [
    emit('vfx:status_effect', {
      targetId: casterId, effectId: aura.effectId, abilityId, active: true, duration: aura.durationMs,
      ...(aura.color !== undefined ? { color: aura.color } : {}),
    } as EventPayloadMap['vfx:status_effect']),
    emit('vfx:status_effect', { targetId: casterId, effectId: aura.effectId, abilityId, active: false } as EventPayloadMap['vfx:status_effect'], aura.durationMs),
  ];
}

/** The orbit of stars around the body (it rides the caster). */
export function orbitPlan(orbit: OrbitGeometry, casterId: string, body: { x: number; y: number }, startTime: number): VisualEmit[] {
  return [emit('vfx:lucky_seven_orbit', {
    casterId, targetId: casterId, x: body.x, y: body.y, numStars: orbit.numStars, radius: orbit.radius, duration: orbit.durationMs, startTime,
  })];
}

/** The withVFX beat at the body centre. */
export function beatPlan(beat: BeatGeometry, abilityId: string, casterId: string, body: { x: number; y: number }): VisualEmit[] {
  return [emit('vfx:spawn', {
    effectId: beat.effectId, x: body.x, y: body.y, duration: beat.durationMs, color: beat.color, scale: beat.scale,
    abilityId, casterId, follow: false,
  })];
}

/** True when the cast's area lands where a travel dash ends (Heroic Leap). */
export function landsOnArrival(geom: CastGeometry): boolean {
  return !!geom.area && (geom.dash?.travelMs ?? 0) > 0;
}

/**
 * Everything a cast draws at its start, for a pose: beat, caster aura, orbit,
 * swing (unless a dash carries it to the arrival), area, zone (with an
 * immediate/timed trigger burst), cone and line.
 * `swingTargetId` is the contact a lightning swing reaches (only a target
 * inside the cone).
 */
export function castVisualPlan(geom: CastGeometry, pose: CastPose, opts: { swingTargetId?: string; now?: number } = {}): VisualEmit[] {
  const { abilityId, casterId, body } = pose;
  const plan: VisualEmit[] = [];
  if (geom.beat) plan.push(...beatPlan(geom.beat, abilityId, casterId, body));
  if (geom.casterAura) plan.push(...casterAuraPlan(geom.casterAura, abilityId, casterId));
  if (geom.orbit) plan.push(...orbitPlan(geom.orbit, casterId, body, opts.now ?? Date.now()));
  // A dash's swing lands where the dash ends (Charge, Shield Charge): an
  // observer draws it on the `dashed` arrival (RemoteCastVFXHandler), not here.
  if (geom.swing && !geom.dash) {
    plan.push(emit('vfx:melee_swing', meleeSwingPayload(abilityId, pose.heroClass, body, pose.facingRight, geom.swing, opts.swingTargetId)));
  }
  // A travel dash's area is its landing slam (Heroic Leap): drawn on the
  // arrival (RemoteCastGeometryPlayer.playArrival), where it hits (lane BX).
  if (geom.area && !landsOnArrival(geom)) plan.push(...areaPlan(geom.area, abilityId, casterId, areaCentre(geom.area, pose)));
  if (geom.zone) {
    const at = zonePlacement(geom.zone, pose);
    const instanceId = nextInstanceId('summon', abilityId, casterId);
    plan.push(...zonePlan(geom.zone, abilityId, at, instanceId));
    if (geom.zone.trigger === 'immediate') plan.push(...zoneTriggerEmit(geom.zone, abilityId, at, instanceId, geom.zone.triggerDelayMs));
    else if (geom.zone.trigger === 'timed') plan.push(...zoneTriggerEmit(geom.zone, abilityId, at, instanceId, geom.zone.triggerDelayMs || 1000));
  }
  if (geom.cone) plan.push(...conePlan(geom.cone, abilityId, pose));
  if (geom.line) plan.push(...linePlan(geom.line, lineCentre(geom.line, pose)));
  return plan;
}

/** How long a plan keeps drawing (ms) — the remote budget's lifetime. */
export function planLifetimeMs(plan: VisualEmit[]): number {
  let end = 0;
  for (const e of plan) {
    const p = e.payload as { duration?: number };
    end = Math.max(end, e.delayMs + (typeof p.duration === 'number' ? p.duration : 0));
  }
  return end;
}
